import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { categories, comics } from "@/mocks/comics";
import ComicCard from "@/components/feature/ComicCard";

const categoryDesc: Record<string, string> = {
  校园: "青春日常、校园恋爱与成长故事，带你重温学生时代的美好与悸动。",
  职场: "打工人图鉴与职场生存法则，幽默中带着真实的温度。",
  奇幻: "异界冒险与热血觉醒，宏大的世界观里藏着无限想象。",
  恋爱: "从心动到相守，甜宠日常与细腻情感的完美结合。",
  剧情: "人生百态与治愈成长，每一部都值得慢慢品读。",
};

export default function Category() {
  usePageMeta({
    title: "羞羞漫画韩国 - 校园、职场、奇幻、恋爱、剧情漫画分类浏览",
    description:
      "羞羞漫画韩国题材分类齐全，涵盖校园、职场、奇幻、恋爱、剧情等热门韩国漫画，支持按标签与关键词筛选，快速找到你喜欢的作品。",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("type") ?? "全部";
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    let list = active === "全部" ? comics : comics.filter((c) => c.category === active);
    if (keyword.trim()) {
      const kw = keyword.trim();
      list = list.filter(
        (c) =>
          c.title.includes(kw) ||
          c.author.includes(kw) ||
          c.tags.some((t) => t.includes(kw)) ||
          c.category.includes(kw),
      );
    }
    return list;
  }, [active, keyword]);

  const pickType = (type: string) => {
    setSearchParams(type === "全部" ? {} : { type });
  };

  return (
    <main className="w-full bg-background-50">
      <section className="border-b border-background-200 bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-800">题材分类</span>
          </nav>
          <h1 className="mt-4 font-display text-3xl font-black text-foreground-950 md:text-4xl">
            题材分类
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            {active === "全部"
              ? "全站韩国漫画一览，按题材与标签浏览，轻松找到你的心头好。"
              : categoryDesc[active] ?? "浏览该题材下的韩国漫画作品。"}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="题材切换">
            {["全部", ...categories].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => pickType(c)}
                role="tab"
                aria-selected={active === c}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  active === c
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-72">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" aria-hidden="true"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索标题、作者或标签"
              className="w-full rounded-md border border-background-200 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              aria-label="搜索漫画"
            />
          </div>
        </div>

        <p className="mt-6 text-sm text-foreground-500">
          共 <span className="font-semibold text-primary-600">{filtered.length}</span> 部作品
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {filtered.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center rounded-xl bg-background-100 py-16 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-300" aria-hidden="true"></i>
            <p className="mt-4 text-sm text-foreground-600">没有找到符合条件的作品，换个关键词试试吧。</p>
          </div>
        )}
      </section>
    </main>
  );
}